import { pluck, distinctUntilChanged } from "rxjs/operators"
import { Socket as SocketV2 } from "socket.io-client-v2"
import { Socket as SocketV3 } from "socket.io-client-v3"
import { Socket as SocketV4 } from "socket.io-client-v4"
import DispatchingStore, { defineDispatchers } from "./DispatchingStore"
import {
  HoppRealtimeLog,
  HoppRealtimeLogLine,
} from "~/helpers/types/HoppRealtimeLog"

type SocketIO = SocketV2 | SocketV3 | SocketV4

export type SIOClientVersion = "v4" | "v3" | "v2"

export type SIOKeyValue = {
  id: number
  key: string
  value: string
  description: string
  active: boolean
}

export type SIOEventListener = {
  id: number
  event: string
  description: string
  active: boolean
}

export type SIOMessageArgument = {
  id: number
  type: "text" | "json" | "number" | "boolean"
  value: string
}

export type SIOWorkspaceConfig = {
  message: {
    eventName: string
    acknowledge: boolean
    args: SIOMessageArgument[]
  }
  queryParams: SIOKeyValue[]
  headers: SIOKeyValue[]
  listeners: SIOEventListener[]
  auth: {
    type: "None" | "Bearer"
    token: string
    active: boolean
  }
  settings: {
    timeout: number
    reconnection: boolean
    reconnectionAttempts: number
    reconnectionDelay: number
    rejectUnauthorized: boolean
  }
}

export type HoppSIORequest = {
  endpoint: string
  path: string
  version: SIOClientVersion
  config: SIOWorkspaceConfig
  log: HoppRealtimeLog
}

export const defaultSIOConfig: SIOWorkspaceConfig = {
  message: {
    eventName: "",
    acknowledge: false,
    args: [{ id: 1, type: "text", value: "" }],
  },
  queryParams: [
    { id: 1, key: "", value: "", description: "", active: false },
  ],
  headers: [
    { id: 1, key: "", value: "", description: "", active: false },
  ],
  listeners: [{ id: 1, event: "", description: "", active: false }],
  auth: { type: "None", token: "", active: true },
  settings: {
    timeout: 20000,
    reconnection: true,
    reconnectionAttempts: 3,
    reconnectionDelay: 1000,
    rejectUnauthorized: true,
  },
}

type HoppSIOSession = {
  request: HoppSIORequest
  log: HoppRealtimeLog
  socket: SocketIO | null
}

const defaultSIORequest: HoppSIORequest = {
  endpoint: "wss://echo-socketio.hoppscotch.io",
  path: "/socket.io",
  version: "v4",
  config: defaultSIOConfig,
  log: [],
}

const defaultSIOSession: HoppSIOSession = {
  request: defaultSIORequest,
  socket: null,
  log: [],
}

const dispatchers = defineDispatchers({
  setRequest(
    _: HoppSIOSession,
    { newRequest }: { newRequest: HoppSIORequest }
  ) {
    return {
      request: newRequest,
      log: newRequest.log,
    }
  },
  setEndpoint(curr: HoppSIOSession, { newEndpoint }: { newEndpoint: string }) {
    return {
      request: {
        ...curr.request,
        endpoint: newEndpoint,
      },
    }
  },
  setPath(curr: HoppSIOSession, { newPath }: { newPath: string }) {
    return {
      request: {
        ...curr.request,
        path: newPath,
      },
    }
  },
  setVersion(
    curr: HoppSIOSession,
    { newVersion }: { newVersion: SIOClientVersion }
  ) {
    return {
      request: {
        ...curr.request,
        version: newVersion,
      },
    }
  },
  setConfig(
    curr: HoppSIOSession,
    { config }: { config: SIOWorkspaceConfig }
  ) {
    return {
      request: { ...curr.request, config },
    }
  },
  setAuth(
    curr: HoppSIOSession,
    { auth }: { auth: Partial<SIOWorkspaceConfig["auth"]> }
  ) {
    return {
      request: {
        ...curr.request,
        config: {
          ...curr.request.config,
          auth: { ...curr.request.config.auth, ...auth },
        },
      },
    }
  },
  setSocket(_: HoppSIOSession, { socket }: { socket: SocketIO }) {
    return {
      socket,
    }
  },
  setLog(_: HoppSIOSession, { log }: { log: HoppRealtimeLog }) {
    return {
      log,
      request: { ..._.request, log },
    }
  },
  addLogLine(curr: HoppSIOSession, { line }: { line: HoppRealtimeLogLine }) {
    return {
      log: [...curr.log, line],
      request: { ...curr.request, log: [...curr.log, line] },
    }
  },
})

const SIOSessionStore = new DispatchingStore(defaultSIOSession, dispatchers)

export function setSIORequest(newRequest?: HoppSIORequest) {
  SIOSessionStore.dispatch({
    dispatcher: "setRequest",
    payload: {
      newRequest: newRequest ?? defaultSIORequest,
    },
  })
}

export function setSIOEndpoint(newEndpoint: string) {
  SIOSessionStore.dispatch({
    dispatcher: "setEndpoint",
    payload: {
      newEndpoint,
    },
  })
}

export function setSIOVersion(newVersion: SIOClientVersion) {
  SIOSessionStore.dispatch({
    dispatcher: "setVersion",
    payload: {
      newVersion,
    },
  })
}

export function setSIOPath(newPath: string) {
  SIOSessionStore.dispatch({
    dispatcher: "setPath",
    payload: {
      newPath,
    },
  })
}

export function setSIOConfig(config: SIOWorkspaceConfig) {
  SIOSessionStore.dispatch({
    dispatcher: "setConfig",
    payload: { config },
  })
}

export function setSIOAuth(auth: Partial<SIOWorkspaceConfig["auth"]>) {
  SIOSessionStore.dispatch({
    dispatcher: "setAuth",
    payload: { auth },
  })
}

export function setSIOSocket(socket: SocketIO) {
  SIOSessionStore.dispatch({
    dispatcher: "setSocket",
    payload: {
      socket,
    },
  })
}

export function setSIOLog(log: HoppRealtimeLog) {
  SIOSessionStore.dispatch({
    dispatcher: "setLog",
    payload: {
      log,
    },
  })
}

export function addSIOLogLine(line: HoppRealtimeLogLine) {
  SIOSessionStore.dispatch({
    dispatcher: "addLogLine",
    payload: {
      line,
    },
  })
}

export const SIORequest$ = SIOSessionStore.subject$.pipe(
  pluck("request"),
  distinctUntilChanged()
)

export const SIOEndpoint$ = SIOSessionStore.subject$.pipe(
  pluck("request", "endpoint"),
  distinctUntilChanged()
)

export const SIOVersion$ = SIOSessionStore.subject$.pipe(
  pluck("request", "version"),
  distinctUntilChanged()
)

export const SIOPath$ = SIOSessionStore.subject$.pipe(
  pluck("request", "path"),
  distinctUntilChanged()
)

export const SIOConfig$ = SIOSessionStore.subject$.pipe(
  pluck("request", "config"),
  distinctUntilChanged()
)

export const SIOConnectionState$ = SIOSessionStore.subject$.pipe(
  pluck("connectionState"),
  distinctUntilChanged()
)

export const SIOSocket$ = SIOSessionStore.subject$.pipe(
  pluck("socket"),
  distinctUntilChanged()
)

export const SIOLog$ = SIOSessionStore.subject$.pipe(
  pluck("log"),
  distinctUntilChanged()
)
